import { Link, useLocation } from "react-router-dom";
import { ArrowRight, Anchor, Bot, Radio, Shield, Scale, Compass } from "lucide-react";

export default function About() {
    const { search } = useLocation();
    return (
        <div data-testid="about-page" className="mx-auto max-w-[1200px] px-6 py-20 md:px-10">
            {/* Header */}
            <header className="max-w-3xl">
                <div className="caption text-[var(--brand-accent)]">The Sovereign Blueprint · About</div>
                <h1 className="mt-3 font-serif text-5xl tracking-display text-[#FAFAFA] sm:text-7xl leading-[0.95]">
                    Built for the <em className="italic" style={{ color: "var(--brand-accent)" }}>Privateer</em>.
                    <br />Aligned with the recruiter.
                </h1>
                <p className="mt-8 text-lg font-light leading-relaxed text-[#A3A3A3]">
                    Most platforms harvest the recruiter. We commission them. The Sovereign Recruitment & White-Label Hub
                    is a fleet of armored, self-branded recruitment hulls — each operator boards once, signs a Covenant,
                    and ships under a revenue law written for the people who do the work, not the people who hold the keys.
                </p>
            </header>

            {/* 60/30/10 LAW callout */}
            <section
                data-testid="law-callout"
                className="mt-16 overflow-hidden rounded-md border p-8 md:p-12"
                style={{
                    borderColor: "rgba(201,169,110,0.45)",
                    background:
                        "linear-gradient(135deg, rgba(201,169,110,0.10) 0%, rgba(10,10,10,0.6) 60%, rgba(10,10,10,1) 100%)",
                }}
            >
                <div className="flex items-center gap-3">
                    <Scale size={20} style={{ color: "var(--brand-accent)" }} />
                    <span className="caption tracking-widest" style={{ color: "var(--brand-accent)" }}>
                        Revenue Constitution · Inviolable
                    </span>
                </div>
                <h2 className="mt-4 font-serif text-5xl tracking-display text-[#FAFAFA] md:text-7xl">
                    The <span style={{ color: "var(--brand-accent)" }}>60 / 30 / 10</span> LAW
                </h2>
                <p className="mt-4 max-w-3xl text-[#A3A3A3] leading-relaxed md:text-lg">
                    Every dollar a hull collects — boarding fees, placement bounties, retainers — is split by the LAW the moment it lands.
                    Not a fee schedule. Not a negotiation. A constitution.
                </p>
                <div className="mt-10 grid gap-3 md:grid-cols-3">
                    <LawTile pct="60" label="Recruiter" sub="The operator who closes the placement keeps the largest share, every time." accent />
                    <LawTile pct="30" label="Platform" sub="Funds Hull engineering, the Ghost Crew, the Foundry Ledger, and white-label provisioning." />
                    <LawTile pct="10" label="CyberKids Anchor" sub="Routed to youth cybersecurity training — the next generation of operators we owe." />
                </div>
                <p className="mt-8 text-xs text-[#737373]">
                    The LAW is enforced by the Foundry Ledger and acknowledged by every Privateer in writing as Point 3 of the Technical Covenant.
                </p>
            </section>

            {/* Three Pillars */}
            <section className="mt-24">
                <div className="caption text-[var(--brand-accent)]">Three Core Layers</div>
                <h2 className="mt-3 font-serif text-4xl tracking-display text-[#FAFAFA] sm:text-5xl">
                    Hull · Crew · Signal
                </h2>
                <div className="mt-10 grid gap-6 md:grid-cols-3">
                    {[
                        { Icon: Anchor, title: "The Hull", body: "An enterprise-grade, white-labeled recruitment platform on your own subdomain. Your colors, your logo, your brand voice — armored for security and performance." },
                        { Icon: Bot, title: "The Crew", body: "A Ghost Crew of AI agents runs candidate scoring, lead capture, and pipeline ops in the background. The operator stays focused on closing." },
                        { Icon: Radio, title: "The Signal", body: "A pre-wired revenue model with automated 60/30/10 LAW tracking, a Master Command Bridge for ops, and a connection to the Sovereign fleet." },
                    ].map(({ Icon, title, body }) => (
                        <div key={title} className="rounded-md border border-white/5 bg-[#141414]/60 p-7 backdrop-blur-xl">
                            <Icon size={26} style={{ color: "var(--brand-accent)" }} />
                            <h3 className="mt-5 font-serif text-2xl tracking-display text-[#FAFAFA]">{title}</h3>
                            <p className="mt-3 text-[#A3A3A3] leading-relaxed">{body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Trinity Boarding */}
            <section className="mt-24">
                <div className="caption text-[var(--brand-accent)]">Trinity Boarding</div>
                <h2 className="mt-3 font-serif text-4xl tracking-display text-[#FAFAFA] sm:text-5xl">
                    Three gates. One Privateer.
                </h2>
                <ol className="mt-12 space-y-8">
                    {[
                        { n: "01", title: "Director Interview", body: "Tell the Wayfinder your niche, experience, and the subdomain you want for your hull." },
                        { n: "02", title: "First Light Boarding Fee", body: "Pay the $250 boarding fee via PayPal or Solana USDC. The 60/30/10 LAW engages the moment the transaction captures." },
                        { n: "03", title: "Mission Intake", body: "Declare your strike vectors, accept the NDA, lock in your clearance. Real B2B intelligence dossiers unlock on completion." },
                    ].map(({ n, title, body }) => (
                        <li key={n} className="flex gap-8 border-t border-white/5 pt-8">
                            <span className="font-mono text-sm text-[#737373]">{n}</span>
                            <div>
                                <h3 className="font-serif text-2xl tracking-display text-[#FAFAFA]">{title}</h3>
                                <p className="mt-2 max-w-2xl text-[#A3A3A3] leading-relaxed">{body}</p>
                            </div>
                        </li>
                    ))}
                </ol>
            </section>

            {/* The Covenant */}
            <section className="mt-24">
                <div className="flex items-center gap-3">
                    <Shield size={18} style={{ color: "var(--brand-accent)" }} />
                    <span className="caption text-[var(--brand-accent)]">The Technical Covenant</span>
                </div>
                <h2 className="mt-3 font-serif text-4xl tracking-display text-[#FAFAFA] sm:text-5xl">
                    Five points. No exceptions.
                </h2>
                <p className="mt-3 max-w-2xl text-[#A3A3A3]">
                    Operational roles (Technicians + Surgeons) sign the Covenant before reaching the Director's queue.
                    It is enforced as a precondition of boarding.
                </p>
                <div className="mt-10 grid gap-3 md:grid-cols-2">
                    {[
                        ["01", "Technical Mass", "Maintain hands-on proficiency across at least one strike vector. Bench-test before every engagement."],
                        ["02", "Trinity Boarding", "Complete all three boarding gates before claiming an active mission."],
                        ["03", "60 / 30 / 10 LAW", "Honor the LAW. No side-deals. No skim."],
                        ["04", "48-Hour Velocity", "Acknowledge new missions within 48 hours. Silent runs revoke clearance."],
                        ["05", "Steel Box Maintenance", "Operate from a hardened, encrypted workstation. No client data on personal devices."],
                    ].map(([n, t, body]) => (
                        <div key={n} className="rounded-sm border border-white/10 bg-white/[0.02] px-5 py-4">
                            <div className="flex items-baseline gap-3">
                                <span className="caption text-[var(--brand-accent)]">{n}</span>
                                <div className="font-serif text-xl text-[#FAFAFA]">{t}</div>
                            </div>
                            <p className="mt-1.5 text-sm text-[#A3A3A3]">{body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* CTA */}
            <section className="mt-24 flex flex-wrap items-center justify-between gap-6 rounded-md border border-white/5 bg-[#141414]/60 p-10 backdrop-blur-xl">
                <div>
                    <div className="caption text-[var(--brand-accent)]">Ready to board?</div>
                    <h3 className="mt-2 font-serif text-3xl tracking-display text-[#FAFAFA]">
                        The Wayfinder is waiting.
                    </h3>
                </div>
                <div className="flex gap-3">
                    <Link
                        to={{ pathname: "/vision", search }}
                        data-testid="about-cta-vision"
                        className="inline-flex items-center gap-2 rounded-sm border border-white/15 px-6 py-3 text-sm font-medium text-[#FAFAFA] hover:bg-white/[0.04]"
                    >
                        <Compass size={14} /> Read the Vision
                    </Link>
                    <Link
                        to={{ pathname: "/foundry/interview", search }}
                        data-testid="about-cta-interview"
                        className="inline-flex items-center gap-2 rounded-sm px-6 py-3 text-sm font-medium"
                        style={{ background: "var(--brand-accent)", color: "#0A0A0A" }}
                    >
                        Begin the Director Interview <ArrowRight size={14} />
                    </Link>
                </div>
            </section>
        </div>
    );
}

function LawTile({ pct, label, sub, accent }) {
    return (
        <div
            data-testid={`law-tile-${pct}`}
            className={`relative overflow-hidden rounded-sm border p-6 ${accent ? "border-[var(--brand-accent)]/50" : "border-white/10"}`}
            style={accent ? { background: "rgba(201,169,110,0.06)" } : { background: "rgba(255,255,255,0.02)" }}
        >
            <div className="flex items-baseline gap-3">
                <span className="font-serif text-6xl tracking-display" style={{ color: accent ? "var(--brand-accent)" : "#FAFAFA" }}>
                    {pct}
                </span>
                <span className="font-mono text-sm text-[#737373]">%</span>
            </div>
            <div className="mt-2 caption text-[#FAFAFA]">{label}</div>
            <p className="mt-2 text-xs text-[#A3A3A3]">{sub}</p>
        </div>
    );
}
